/**
 * @fileoverview Thin data-provider wrapper for the TetherSync widget in system-web.
 * @module system-web/components/DefensibleDataWidget
 *
 * Responsibilities:
 * - Read storage health, collection sync state, push queue, and conflicts from
 *   system-web's local-first data sources.
 * - Map that data to TetherSyncWidgetProps and delegate all rendering to the
 *   shared TetherSyncWidget from @firesquirrel/local-db.
 * - Manage the ConflictResolutionPanel open/close state.
 *
 * Dependencies:
 * - useStorageHealthPolling: IndexedDB quota and persistence polling.
 * - useSystemDb: collectionStatuses, syncing flag, conflicts, forceResync.
 * - subscribeSyncStatusV2: push queue status from the shared sync status store.
 * - triggerFullSync: triggers an immediate delta sync cycle (onSyncNow).
 * - forceResync (from context): clears all sync checkpoints and triggers full re-download
 *   (onForceResync). Requires inline confirmation in TetherSyncWidget.
 * - TetherSyncWidget: shared UI component from @firesquirrel/local-db.
 * - ConflictResolutionPanel: slideout for reviewing and resolving sync conflicts.
 *
 * Architecture:
 * - Local-first UI only — all data comes from Dexie-backed contexts and polling.
 *   No API calls are made from this component.
 * - This wrapper contains ZERO rendering logic; it is a pure data-binding layer.
 * - Button parity: both onSyncNow (delta) and onForceResync (full) are passed so the
 *   panel matches tenant-web and mobile-pwa identically.
 *
 * @localFirst
 * Source of Truth: local
 * Write Flow: n/a (read-only aggregation)
 * Sync Strategy: subscribes to SystemDb context, storage polling, and sync status store
 * Conflict Resolution: delegates to ConflictResolutionPanel
 */

import { useState, useEffect } from 'react';
import {
  TetherSyncWidget,
  subscribeSyncStatusV2,
  useStorageHealthPolling,
  triggerFullSync,
} from '@firesquirrel/local-db';
import type { CollectionSyncRow, SyncStatusV2 } from '@firesquirrel/local-db';
import { useSystemDb } from './SystemDBProvider';
import ConflictResolutionPanel from './ConflictResolutionPanel';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const COLLECTION_LABELS: Record<string, string> = {
  tenants: 'Departments',
  organizations: 'Organizations',
  system_users: 'Users',
  system_roles: 'Roles',
  tiers: 'Tiers',
  feature_flags: 'Feature Flags',
  modules: 'Modules',
  system_lookups: 'Lookups',
  onboarding_drafts: 'Drafts',
  import_templates: 'Templates',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component DefensibleDataWidget
 *
 * Purpose:
 * - Thin data-provider wrapper for the shared TetherSyncWidget in system-web.
 * - Aggregates storage health, collection sync state, SSE stream status, push
 *   queue status, and conflict data from system-web's local-first data sources.
 *
 * Props:
 * - None (all state comes from context hooks and store subscriptions).
 *
 * Behavior:
 * - Reads from useSystemDb, useStorageHealthPolling, and subscribeSyncStatusV2.
 * - Maps all data to TetherSyncWidgetProps and renders TetherSyncWidget.
 * - Passes triggerFullSync as onSyncNow (delta pull) and forceResync as
 *   onForceResync (full checkpoint-clear re-download), matching tenant-web
 *   and mobile-pwa button parity exactly.
 * - Manages ConflictResolutionPanel open/close state locally.
 *
 * Data Source:
 * - local (Dexie context + storage quota API + in-package sync status store).
 *
 * Constraints:
 * - MUST NOT call any API endpoints directly.
 * - MUST NOT contain any rendering logic — all UI is in TetherSyncWidget.
 */
export default function DefensibleDataWidget() {
  const {
    syncing: anyActive,
    collectionStatuses,
    conflicts,
    forceResync,
    dbStatus,
  } = useSystemDb();
  const {
    health: storageHealth,
    persistence: storagePersistence,
    lastUpdated,
    refresh: refreshStorage,
  } = useStorageHealthPolling(true);

  const [online, setOnline] = useState(navigator.onLine);
  const [isPersisted, setIsPersisted] = useState<boolean | null>(null);
  const [storagePercent, setStoragePercent] = useState(0);
  const [storageUsed, setStorageUsed] = useState(0);
  const [storageQuota, setStorageQuota] = useState(0);
  const [lastChecked, setLastChecked] = useState<string>(new Date().toISOString());
  const [conflictPanelOpen, setConflictPanelOpen] = useState(false);
  const [pushInfo, setPushInfo] = useState({ pending: 0, failed: 0, pushing: false });

  // ── Online/offline tracking ──────────────────────────────────────────────
  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // ── Storage health ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!storagePersistence || !lastUpdated) return;
    setIsPersisted(
      storagePersistence.granted || storagePersistence.outcome === 'already-persisted',
    );
    setStoragePercent(storageHealth.percentUsed);
    setStorageUsed(storageHealth.usage);
    setStorageQuota(storageHealth.quota);
    setLastChecked(lastUpdated);
  }, [lastUpdated, storageHealth, storagePersistence]);

  // ── Push queue subscription ──────────────────────────────────────────────
  useEffect(() => {
    return subscribeSyncStatusV2((s: SyncStatusV2) => {
      setPushInfo({
        pending: s.pendingPushCount,
        failed: s.failedPushCount,
        pushing: s.isPushing,
      });
    });
  }, []);

  // ── Derived data ─────────────────────────────────────────────────────────
  const collections: CollectionSyncRow[] = Object.values(collectionStatuses).map((col) => ({
    name: col.name,
    label: COLLECTION_LABELS[col.name] ?? col.name,
    status: col.status as CollectionSyncRow['status'],
    lastSyncAt: col.lastSyncAt,
    lastError: col.lastError,
  }));

  const unresolvedConflictCount = conflicts.filter((c) => !c.resolved).length;

  return (
    <>
      <TetherSyncWidget
        storagePercent={storagePercent}
        storageUsed={storageUsed}
        storageQuota={storageQuota}
        isPersisted={isPersisted}
        lastChecked={lastChecked}
        onRefreshStorage={() => void refreshStorage('widget-manual-refresh')}
        online={online}
        anyActive={anyActive}
        collections={collections}
        pendingPushCount={pushInfo.pending}
        failedPushCount={pushInfo.failed}
        isPushing={pushInfo.pushing}
        unresolvedConflictCount={unresolvedConflictCount}
        onOpenConflicts={() => setConflictPanelOpen(true)}
        onSyncNow={() => void triggerFullSync()}
        onForceResync={forceResync}
        dbStatus={dbStatus}
      />
      <ConflictResolutionPanel
        open={conflictPanelOpen}
        onClose={() => setConflictPanelOpen(false)}
      />
    </>
  );
}
